/**
 * My Access (Round 1.4 — A5.4).
 *
 * What the signed-in user can see and do, plus a kick-off button for the
 * Access-Request wizard. Three sections:
 *
 *   1. Identity — email, role, effective WGs (live from /api/v1/me).
 *   2. Available — servers + tools the user can call today (from
 *      /api/v1/catalog/{servers,tools}; backend filters by WG).
 *   3. Requestable — tools the gateway sees but the user is missing a WG
 *      for; clicking one preloads the wizard with that asset.
 */
import { useMemo, type JSX } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Boxes, KeyRound, Plus, ShieldQuestion, UserCheck } from 'lucide-react';

import { useCatalogServers, useCatalogTools } from '@/api/index.js';
import { useAuth } from '@/auth/AuthContext.js';
import { ActionButton } from '@/components/console/ActionButton.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { SectionCard } from '@/components/console/SectionCard.js';
import { AsyncShell } from '@/components/console/StateBlocks.js';

interface MeResponse {
  email: string;
  display_name: string | null;
  role: 'admin' | 'viewer' | 'bootstrap' | null;
  is_member: boolean;
  is_admin: boolean;
  effective_wgs?: readonly string[];
}

export default function MyAccess(): JSX.Element {
  const { me } = useAuth();
  const meExt = me as MeResponse | null;
  const [params] = useSearchParams();
  const vsSlug = params.get('vs') || undefined;
  const tools = useCatalogTools({ virtualServerSlug: vsSlug });
  const servers = useCatalogServers();

  const wgs = meExt?.effective_wgs ?? [];
  const bundleName = tools.data?.virtual_server?.name;
  const requestableForBundle = tools.data?.requestable_for_bundle ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Discover"
        title={bundleName ? `My Access — ${bundleName}` : 'My Access'}
        description={
          bundleName
            ? `Scoped to virtual server "${vsSlug}". Tools you can call inside this bundle, plus tools your WG allows but the bundle excludes.`
            : 'Your identity, what you can call, and what you can request.'
        }
      >
        <Link to="/access/request">
          <ActionButton icon={ShieldQuestion} primary>
            Request access
          </ActionButton>
        </Link>
      </PageHeader>

      <SectionCard icon={UserCheck} title="Identity" text="Driven by your Stanford Google sign-in.">
        {!me ? (
          <p className="subtle">Not signed in.</p>
        ) : (
          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              rowGap: 8,
              columnGap: 16,
              margin: 0,
              fontSize: 15,
            }}
          >
            <dt className="subtle">Email</dt>
            <dd>
              <code>{me.email}</code>
            </dd>
            <dt className="subtle">Display name</dt>
            <dd>{me.display_name ?? '—'}</dd>
            <dt className="subtle">Role</dt>
            <dd>
              <strong>{me.role ?? 'viewer'}</strong>
            </dd>
            <dt className="subtle">Portal member</dt>
            <dd>{me.is_member ? 'yes' : 'no'}</dd>
            <dt className="subtle">Admin</dt>
            <dd>{me.is_admin ? 'yes' : 'no'}</dd>
            <dt className="subtle">Effective WGs</dt>
            <dd>
              {wgs.length === 0 ? (
                <span className="subtle">
                  No workgroup memberships found. Ask an admin if you expect to be in one.
                </span>
              ) : (
                <span style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {wgs.map((wg) => (
                    <code
                      key={wg}
                      style={{
                        padding: '2px 8px',
                        borderRadius: 6,
                        background: 'rgba(0,0,0,0.06)',
                        fontSize: 13,
                      }}
                    >
                      {wg}
                    </code>
                  ))}
                </span>
              )}
            </dd>
          </dl>
        )}
      </SectionCard>

      <SectionCard
        icon={Boxes}
        title="Available servers"
        text="MCP servers the gateway will route your calls to. The catalog is already filtered by your effective WG set."
      >
        <AsyncShell
          isLoading={servers.isLoading}
          isError={servers.isError}
          isEmpty={(servers.data?.items.length ?? 0) === 0}
          error={servers.error}
          loadingTitle="Loading servers"
          errorTitle="Catalog API unreachable"
          emptyTitle="No servers visible to you"
          emptyHint="Either no servers are registered, or you're not in any WG with access."
        >
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 15, lineHeight: 1.6 }}>
            {(servers.data?.items ?? []).map((s) => (
              <li key={s.id}>
                <Link to={`/catalog/toolkits/${s.slug}`}>{s.name}</Link>{' '}
                <span className="subtle" style={{ fontSize: 13 }}>
                  · {s.owningWgId} · risk: {s.riskTier}
                </span>
              </li>
            ))}
          </ul>
        </AsyncShell>
      </SectionCard>

      {bundleName && (
        <SectionCard
          icon={Plus}
          title={`Requestable for bundle "${vsSlug}" (${requestableForBundle.length})`}
          text="Tools your WG allows you to call, but this virtual-server bundle excludes. Ask the bundle owner to add them via composition."
        >
          <AsyncShell
            isLoading={tools.isLoading}
            isError={tools.isError}
            isEmpty={requestableForBundle.length === 0}
            error={tools.error}
            loadingTitle="Loading bundle composition"
            errorTitle="Catalog API unreachable"
            emptyTitle="Nothing to add to this bundle"
            emptyHint="Either every tool your WG allows is already in the bundle, or you have no other accessible tools."
          >
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 15, lineHeight: 1.6 }}>
              {requestableForBundle.map(({ tool }) => (
                <li key={tool.id} style={{ marginBottom: 4 }}>
                  <code>{tool.name}</code>
                  {tool.display_name && tool.display_name !== tool.name && (
                    <span className="subtle" style={{ fontSize: 13 }}>
                      {' '}
                      · {tool.display_name}
                    </span>
                  )}{' '}
                  <Link
                    to={`/access/request?asset_type=access_request&asset_id=${encodeURIComponent(`${vsSlug}:${tool.id}`)}`}
                    style={{ marginLeft: 8 }}
                  >
                    request to add to bundle{' '}
                    <ArrowRight size={12} aria-hidden style={{ verticalAlign: 'middle' }} />
                  </Link>
                </li>
              ))}
            </ul>
          </AsyncShell>
        </SectionCard>
      )}

      <SectionCard
        icon={KeyRound}
        title={`Requestable tools (${tools.data?.requestable.length ?? 0})`}
        text="Tools you don't yet have access to. Each row says which WG you'd need — request it via the wizard."
      >
        <AsyncShell
          isLoading={tools.isLoading}
          isError={tools.isError}
          isEmpty={(tools.data?.requestable.length ?? 0) === 0}
          error={tools.error}
          loadingTitle="Loading catalog"
          errorTitle="Catalog API unreachable"
          emptyTitle="Nothing requestable right now"
          emptyHint="Either you already have access to everything, or the catalog has nothing tagged requestable for your role."
        >
          <RequestableList items={tools.data?.requestable ?? []} />
        </AsyncShell>
      </SectionCard>
    </>
  );
}

interface RequestableEntry {
  tool: { id: string; name: string; display_name?: string };
  missing_wg?: string;
}

function RequestableList({ items }: { items: readonly RequestableEntry[] }): JSX.Element {
  const grouped = useMemo(() => {
    const m = new Map<string, RequestableEntry[]>();
    for (const it of items) {
      const key = it.missing_wg ?? '(unknown)';
      const list = m.get(key) ?? [];
      list.push(it);
      m.set(key, list);
    }
    return Array.from(m.entries());
  }, [items]);

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {grouped.map(([wg, list]) => (
        <div key={wg}>
          <p style={{ margin: '0 0 6px', fontSize: 14 }}>
            Missing WG: <code>{wg}</code>
          </p>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 15 }}>
            {list.map((it) => (
              <li key={it.tool.id} style={{ marginBottom: 4 }}>
                <code>{it.tool.name}</code>{' '}
                {it.tool.display_name && it.tool.display_name !== it.tool.name && (
                  <span className="subtle" style={{ fontSize: 13 }}>
                    · {it.tool.display_name}
                  </span>
                )}{' '}
                <Link
                  to={`/access/request?asset_type=access_request&asset_id=${encodeURIComponent(it.tool.id)}`}
                  style={{ marginLeft: 8 }}
                >
                  request <ArrowRight size={12} aria-hidden style={{ verticalAlign: 'middle' }} />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
